// @givmo/app-admin — the match-policy onboarding dismissal device flag.
//
// Per-browser, PER-COMPANY localStorage flag set when the admin clicks "Save and finish later"
// on the match-policy onboarding wizard. While set, the wizard does not pop again on this device
// for that company. Ports the Flutter `OnboardingDismissal` (SharedPreferences →
// `givmo.onboarding.dismissed.<companyId>`); the React admin app uses its own stable key prefix.
//
// SSR / privacy-mode guarded exactly like `apps/donate/app/lib/donation-storage.ts`: every access
// no-ops (never throws) when `window`/`localStorage` is unavailable (server render, sandboxed
// iframe, Safari private mode). On a new device the flag is absent — multi-device parity would
// need a backend column, which (like Flutter) we explicitly opted against.

/** Build the exact, per-company localStorage key. */
function dismissalKey(companyId: string): string {
  return `givmoAdmin.onboardingDismissed.${companyId}`;
}

/**
 * The localStorage handle, or `null` on the server / where storage is blocked. Accessing
 * `window.localStorage` can throw (privacy modes, sandboxed iframes), so it is guarded.
 */
function storage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Whether the wizard has been dismissed on this device for `companyId`. `false` when unavailable. */
export function isOnboardingDismissed(companyId: string): boolean {
  const store = storage();
  if (store === null) return false;
  try {
    return store.getItem(dismissalKey(companyId)) === 'true';
  } catch {
    return false;
  }
}

/** Persist the per-company dismissal flag. No-op (never throws) when storage is unavailable. */
export function dismissOnboarding(companyId: string): void {
  const store = storage();
  if (store === null) return;
  try {
    store.setItem(dismissalKey(companyId), 'true');
  } catch {
    // Privacy mode / quota — best-effort; the wizard will simply pop again next load.
  }
}

/** Clear the per-company dismissal flag. No-op (never throws) when storage is unavailable. */
export function clearOnboardingDismissal(companyId: string): void {
  const store = storage();
  if (store === null) return;
  try {
    store.removeItem(dismissalKey(companyId));
  } catch {
    // Best-effort.
  }
}
